// src/services/ai.ts
import { GoogleGenAI } from '@google/genai';
import type { FishScanResult } from '../types';

/**
 * safeAI(prompt, apiKey?)
 * - If apiKey is provided, uses it
 * - Otherwise falls back to import.meta.env.VITE_AI_KEY
 * - Always returns a string ('' on failure) so callers don't explode
 */
export async function safeAI(prompt: string, apiKey?: string): Promise<string> {
  try {
    const key =
      (apiKey && apiKey.trim().length > 10 ? apiKey : undefined) ||
      ((import.meta as any)?.env?.VITE_AI_KEY as string | undefined) ||
      ((import.meta as any)?.env?.VITE_AI_KEY as string | undefined);

    if (!key) return '';

    const genai = new GoogleGenAI({ apiKey: key });

    // If your package version only supports one of these, keep the one that works in your build.
    const model =
      (genai as any).getGenerativeModel?.({ model: 'gemini-1.5-flash' }) ??
      (genai as any).models?.get?.('gemini-1.5-flash');

    const result = await (model as any).generateContent(prompt);

    // Depending on SDK version, the text accessor differs:
    const text =
      result?.response?.text?.() ??
      result?.response?.text ??
      result?.text ??
      '';

    return typeof text === 'string' ? text : String(text || '');
  } catch (e) {
    console.warn('[safeAI] failed', e);
    return '';
  }
}

export type FishPhotoScanContext = {
  waterName?: string;
  location?: string;
  lat?: number;
  lon?: number;
  nearbySpecies?: string[];
  expectedSpecies?: string;
  weather?: string;
};

const clampConfidence = (value: unknown, fallback = 0.35): number => {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(0, Math.min(1, n > 1 ? n / 100 : n));
};

const optionalPositiveNumber = (value: unknown): number | undefined => {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) && n > 0 ? n : undefined;
};

const parseJsonObject = (text: string): any | null => {
  if (!text?.trim()) return null;

  try {
    return JSON.parse(text);
  } catch {}

  const match = text.match(/\{[\s\S]*\}/);
  if (!match) return null;

  try {
    return JSON.parse(match[0]);
  } catch {
    return null;
  }
};

const normalizeFishScanResult = (raw: any): FishScanResult => {
  const species = String(raw?.species || raw?.commonName || 'Unknown fish').trim() || 'Unknown fish';

  return {
    species,
    confidence: clampConfidence(raw?.confidence, 0.18),
    lengthInches: optionalPositiveNumber(raw?.lengthInches ?? raw?.length ?? raw?.estimatedLengthInches),
    lengthConfidence: raw?.lengthConfidence == null ? undefined : clampConfidence(raw.lengthConfidence, 0.2),
    weightLbs: optionalPositiveNumber(raw?.weightLbs ?? raw?.weight ?? raw?.estimatedWeightLbs),
    weightConfidence: raw?.weightConfidence == null ? undefined : clampConfidence(raw.weightConfidence, 0.18),
    notes: String(
      raw?.notes ||
        'Photo-only estimate. Use a ruler, scale, and local regulations when accuracy matters.'
    ).trim(),
  };
};

/**
 * FishIQ Scan Phase 1.
 *
 * This is a safe photo -> AI -> prefill helper. It intentionally treats size/weight
 * as rough estimates and falls back to a low-confidence mock result when AI is not
 * configured, so LogCatchView remains production-stable in dev/prod.
 */
export async function analyzeFishPhoto(
  imageUrl: string,
  context: FishPhotoScanContext = {},
  apiKey?: string,
): Promise<FishScanResult> {
  const nearby = context.nearbySpecies?.filter(Boolean).slice(0, 10) ?? [];

  const prompt = `
You are FishIQ Scan for Roddo. Analyze this fish catch photo URL and return ONLY valid JSON.

Photo URL: ${imageUrl}
Water/location context: ${context.waterName || context.location || 'unknown'}
Coordinates: ${typeof context.lat === 'number' && typeof context.lon === 'number' ? `${context.lat}, ${context.lon}` : 'unknown'}
Nearby likely species: ${nearby.length ? nearby.join(', ') : 'unknown'}
Expected species from the active FishIQ/log flow: ${context.expectedSpecies || 'unknown'}
Weather/context: ${context.weather || 'unknown'}

Return this exact shape:
{
  "species": "common species guess",
  "confidence": 0.0,
  "lengthInches": 0,
  "lengthConfidence": 0.0,
  "weightLbs": 0,
  "weightConfidence": 0.0,
  "notes": "short cautionary note"
}

Rules:
- Be conservative. If uncertain, lower confidence.
- Length and weight are approximate because there is no calibrated reference object.
- Never copy the expected species or nearby-species context unless the image itself supports it.
- If the image cannot actually be inspected, return species as "Unknown fish" with confidence <= 0.20.
- Do not claim certainty.
`.trim();

  const text = await safeAI(prompt, apiKey);
  const parsed = parseJsonObject(text);

  if (parsed) {
    return normalizeFishScanResult(parsed);
  }

  // Stable fallback for local dev, missing API keys, model parse failures, or
  // environments where the model cannot actually inspect the image. Never
  // convert catch history / nearby context into a fake species identification.
  return normalizeFishScanResult({
    species: 'Unknown fish',
    confidence: 0.18,
    notes: 'FishIQ Scan could not confidently identify this fish from the photo. The photo is attached; please verify species, length, and weight manually.',
  });
}
